import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  ChevronLeft, 
  Radio, 
  Users, 
  AudioWaveform,
  Settings,
  Logs,
  TriangleAlert,
  ImagePlay,
  EyeOff,
  SmartphoneNfc,
  ListMusic,
  Tag,
  FlagTriangleRight,
  Siren,
  Tags,
  Menu,
  X,
  ArrowLeft
} from 'lucide-react';
import GlobalSettings from "./GlobalSettings";
import KeywordsSection from "./KeywordsSection";
import HallucinationsSection from "./HallucinationsSection";
import FrequencyManagement from "./FrequencyManagement";
import ChannelSettings from "./ChannelSettings";
import UserManagement from "../Users/index";
import F1TerminalLogs from '../Logs/F1TerminalLogs';
import Branding from './Branding';
import DangerZone from './DangerZone';
import SimpleTagManager from './MasterTagKeyManagement';
import ReportsComponent from './ReportsManagement';
import ScannerTable from './ScannerTabel';
import AudioLevelVisualizer from './AudioLevelVisualizer';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from "../AuthContext";

const SettingsPage = ({ isDarkMode, timezone, timeFormat, setTimeFormat, reverseSort, setReverseSort }) => {
  const [loading, setLoading] = useState(true);
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [activeSection, setActiveSection] = useState('channel');
  const [globalSettings, setGlobalSettings] = useState({
    target_language: "english",
    model: "medium.en",
    hallucination: "True",
    transcribe_local: "medium.en"
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toastIdRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const edgeServerEndpoint = localStorage.getItem("EDGE_SERVER_ENDPOINT") || 
                           process.env.REACT_APP_EDGE_SERVER_ENDPOINT || "";
  const API_BASE_URL = `${edgeServerEndpoint}`;
  const { logout, user } = useAuth();

  console.log("SidebarFooter user:", user.username);

  // Define all sidebar items
  const allSidebarItems = [
    { id: 'channel', label: 'Channels', icon: Radio },
    { id: 'frequency', label: 'Frequencies', icon: AudioWaveform },
    { id: 'global', label: 'Global', icon: Settings },
    { id: 'keywords', label: 'Keywords', icon: Siren },
    { id: 'hallucination', label: 'Hallucination', icon: EyeOff },
    { id: 'user', label: 'Users', icon: Users },
    { id: 'Branding', label: 'Branding', icon: ImagePlay },
    { id: 'danger', label: 'Danger Zone', icon: TriangleAlert },
    { id: 'tag', label: 'Tag manager', icon: Tags },
    // { id: 'reports', label: 'My Reports', icon: FlagTriangleRight },
    { id: 'Logs', label: 'Logs', icon: Logs },
  ];

  // Filter sidebar items based on role
  const sidebarItems = userRole === 'member'
    ? allSidebarItems.filter(item => ['keywords', 'frequency', 'reports'].includes(item.id))
    : userRole === null 
    ? [] // Empty array while loading
    : allSidebarItems;

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/users/${user.username}`);
        setUserRole(response.data[user.username]?.role || 'member');
      } catch (error) {
        console.error('Error fetching user role:', error);
        setUserRole('member'); // Fallback to 'member' if API call fails
      }
    };
    fetchUserRole();
  }, [user.username, API_BASE_URL]);

  // Handle URL parameters and set default active section
  useEffect(() => {
    // Don't process if userRole is still loading
    if (userRole === null) {
      return;
    }

    const tabFromUrl = searchParams.get('tab');
    console.log('URL tab parameter:', tabFromUrl);
    console.log('Current user role:', userRole);
    console.log('Available sidebar items:', sidebarItems.map(item => item.id));
    
    if (tabFromUrl) {
      // Check if the tab is valid for the current user role
      const isValidTab = sidebarItems.some(item => item.id === tabFromUrl);
      console.log('Is valid tab:', isValidTab);
      if (isValidTab) {
        setActiveSection(tabFromUrl);
        return;
      }
    }
    
    // If no valid tab in URL, set default based on user role
    if (userRole === 'member') {
      setActiveSection('frequency');
      // Update URL to reflect the default tab
      setSearchParams({ tab: 'frequency' });
    } else {
      // For non-members, default to channel if no tab specified
      if (!tabFromUrl) {
        setSearchParams({ tab: 'channel' });
      }
    }
  }, [userRole, searchParams, setSearchParams, sidebarItems]);

  // Handle resize for mobile-to-desktop transition
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isSidebarOpen]);

  const showToast = useCallback((message, type = 'success') => {
    // Prevent multiple rapid calls
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    
    // Dismiss any existing toast safely
    if (toastIdRef.current) {
      try {
        toast.dismiss(toastIdRef.current);
      } catch (error) {
        console.error('Error dismissing existing toast:', error);
      }
      toastIdRef.current = null;
    }
    
    toastTimeoutRef.current = setTimeout(() => {
      const toastOptions = {
        position: 'top-right',
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: isDarkMode ? "dark" : "light",
        onClose: () => { 
          // Safely clear the reference
          toastIdRef.current = null; 
        },
        onOpen: () => {
          // Ensure we have a valid toast ID
          if (!toastIdRef.current) {
            toastIdRef.current = null;
          }
        }
      };
      
      try {
        const toastId = type === 'success' 
        ? toast.success(message, toastOptions)
        : toast.error(message, toastOptions);
        
        // Only set the ID if the toast was created successfully
        if (toastId) {
          toastIdRef.current = toastId;
        }
      } catch (error) {
        console.error('Toast error:', error);
        toastIdRef.current = null;
      }
    }, 100);
  }, [isDarkMode]);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
        toastTimeoutRef.current = null;
      }
      if (toastIdRef.current) {
        try {
          toast.dismiss(toastIdRef.current);
        } catch (error) {
          console.error('Error dismissing toast:', error);
        }
        toastIdRef.current = null;
      }
    };
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/settings`);
      const settingsData = response.data;
      setGlobalSettings({
        target_language: settingsData.global_target_language || "english",
        model: settingsData.global_model || "medium.en",
        transcribe_local: settingsData.global_transcribe_local || "true",
        transcribe_openai: settingsData.global_transcribe_openai || "true",
        transcribe_node: settingsData.global_transcribe_node || "false",
        hallucination: settingsData.global_hallucination || "true",
        timezone: settingsData.global_timezone || "UTC"
      });
      setKeywords(Array.isArray(settingsData.keywords) ? settingsData.keywords : []);
    } catch (error) {
      console.error('Error fetching settings:', error);
      showToast('Error loading settings!', 'error');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await fetchSettings();
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleAddKeyword = async () => {
    const keyword = newKeyword.trim();
    if (!keyword) return;
    try {
      const response = await axios.post(`${API_BASE_URL}/settings/keywords`, { keyword });
      setKeywords(response.data?.keywords || [...keywords, keyword]);
      setNewKeyword('');
      showToast('Keyword added successfully!');
    } catch (error) {
      console.error('Error adding keyword:', error);
      showToast(error.response?.data?.error || 'Error adding keyword!', 'error');
    }
  };

  const handleRemoveKeyword = async (keyword) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/settings/keywords/${keyword}`);
      setKeywords(response.data?.keywords || keywords.filter(k => k !== keyword));
      showToast('Keyword removed successfully!');
    } catch (error) {
      console.error('Error removing keyword:', error);
      showToast(error.response?.data?.error || 'Error removing keyword!', 'error');
    }
  };

  // Helper function to navigate to a specific tab
  const navigateToTab = (tabId) => {
    setActiveSection(tabId);
    setSearchParams({ tab: tabId });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className={`${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-800'} min-h-screen flex flex-col md:flex-row`}>

      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button 
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className={`p-2 rounded-md shadow-lg ${
            isDarkMode 
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
              : 'bg-white text-gray-600 hover:bg-gray-100'
          }`}
        >
          {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Sidebar */}
      <div
        className={`sidebar-container
          ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} 
          border-r w-64 flex-shrink-0
          lg:static lg:h-auto
          fixed top-0 left-0 h-full
          transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 z-40`}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Settings className="w-6 h-6 text-blue-600" />
                <h1 className="text-xl font-semibold">Settings</h1>
              </div>
              <button
                onClick={() => {
                  navigate("/");
                  setIsSidebarOpen(false);
                }}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode 
                    ? 'text-gray-400 hover:text-white hover:bg-gray-700' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
                title="Back to Dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <nav className="flex-1 p-4 overflow-y-auto">
            <div className="space-y-1">
              {sidebarItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveSection(item.id);
                      setSearchParams({ tab: item.id });
                      setIsSidebarOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left ${
                      activeSection === item.id 
                        ? 'bg-blue-600 text-white' 
                        : `${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </nav>
        </div>
      </div>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="bg-gray-100">
        {/* <div className="p-8 md:p-8 pt-20 md:pt-8"> */}
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg  p-6`}>
            {activeSection === 'channel' && (
              <ChannelSettings isDarkMode={isDarkMode} edgeServerEndpoint={edgeServerEndpoint} />
            )}
            {activeSection === 'global' && (
              <GlobalSettings
                globalSettings={globalSettings}
                isDarkMode={isDarkMode}
                timezone={timezone}
                timeFormat={timeFormat}
                setTimeFormat={setTimeFormat}
                reverseSort={reverseSort}
                setReverseSort={setReverseSort}
                user={user}
                edgeServerEndpoint={edgeServerEndpoint}
                handleGlobalChange={async (field, value) => {
                  try {
                    await axios.put(`${API_BASE_URL}/settings`, { [`global_${field}`]: value });
                    setGlobalSettings(prev => ({ ...prev, [field]: value }));
                    showToast('Global settings updated successfully!');
                  } catch (error) {
                    console.error('Error updating global settings:', error);
                    showToast('Error updating global settings!', 'error');
                  }
                }}
              />
            )}
            {activeSection === 'hallucination' && (
              <HallucinationsSection
                keywords={keywords}
                newKeyword={newKeyword}
                setNewKeyword={setNewKeyword}
                handleAddKeyword={handleAddKeyword}
                handleRemoveKeyword={handleRemoveKeyword}
                isDarkMode={isDarkMode}
              />
            )}
            {activeSection === 'keywords' && (
              <KeywordsSection
                keywords={keywords}
                newKeyword={newKeyword}
                setNewKeyword={setNewKeyword}
                handleAddKeyword={handleAddKeyword}
                handleRemoveKeyword={handleRemoveKeyword}
                isDarkMode={isDarkMode}
              />
            )}
            {activeSection === 'frequency' && (
              <FrequencyManagement edgeServerEndpoint={edgeServerEndpoint} isDarkMode={isDarkMode} />
            )}
            {activeSection === 'user' && (
              <UserManagement edgeServerEndpoint={edgeServerEndpoint} isDarkMode={isDarkMode} />
            )}
            {activeSection === 'scanner' && (
              <ScannerTable edgeServerEndpoint={edgeServerEndpoint} isDarkMode={isDarkMode} />
            )}
            {activeSection === 'tag' && (
              <SimpleTagManager edgeServerEndpoint={edgeServerEndpoint} isDarkMode={isDarkMode} />
            )}
            {/* {activeSection === 'reports' && (
              <ReportsComponent edgeServerEndpoint={edgeServerEndpoint} isDarkMode={isDarkMode} />
            )} */}
            {activeSection === 'audio' && (
              <AudioLevelVisualizer edgeServerEndpoint={edgeServerEndpoint} isDarkMode={isDarkMode} />
            )}
            {activeSection === 'Branding' && (
              <Branding edgeServerEndpoint={edgeServerEndpoint} isDarkMode={isDarkMode} />
            )}
            {activeSection === 'danger' && (
              <DangerZone 
                edgeServerEndpoint={edgeServerEndpoint} 
                isDarkMode={isDarkMode}
                showToast={showToast}
              />
            )}

            {activeSection === 'Logs' && (
              <F1TerminalLogs edgeServerEndpoint={edgeServerEndpoint} isDarkMode={isDarkMode} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;